import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Button } from './ui/button.js';

export function SectionHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description: string;
}) {
  return (
    <div className="space-y-1.5">
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h2 className="font-sans text-[18px] font-semibold tracking-[-0.01em]">
        {title}
      </h2>
      <p className="font-mono text-[11px] text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        className="w-full rounded-md border border-border bg-card px-3 py-2 font-sans text-[13px] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background dark:border-white/10 dark:bg-card-raised"
      />
    </div>
  );
}

export function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
      {children}
    </label>
  );
}

export function Switch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}

export function SaveBar({
  dirty,
  saving,
  error,
  savedAt,
  onSave,
}: {
  dirty: boolean;
  saving: boolean;
  error: string | null;
  savedAt: Date | null;
  onSave: () => void;
}) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 flex justify-center pb-6">
      <div
        className={cn(
          'pointer-events-auto flex items-center gap-4 rounded-lg border border-[hsl(var(--hairline))] bg-card px-4 py-3 shadow-lg transition-opacity',
          !dirty && !saving && 'opacity-0',
        )}
      >
        {error ? (
          <span className="font-mono text-[11px] text-destructive">
            {error}
          </span>
        ) : saving ? (
          <span className="font-mono text-[11px] text-muted-foreground">
            Saving…
          </span>
        ) : dirty ? (
          <span className="font-mono text-[11px] text-muted-foreground">
            Unsaved changes
          </span>
        ) : savedAt ? (
          <span className="font-mono text-[11px] text-success">
            Saved{' '}
            {savedAt.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : null}
        <Button type="button" size="sm" onClick={onSave} disabled={!dirty || saving}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

export function ProptreeFormSkeleton() {
  return (
    <div className="space-y-4">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-40 animate-pulse rounded-lg border border-[hsl(var(--hairline))] bg-card"
        />
      ))}
    </div>
  );
}
